import {visit} from 'unist-util-visit';
import type {Plugin} from 'unified';
import type {Root, Text} from 'mdast';

// Regex to match [[slug]] or [[slug|display text]]
const wikiLinkRegex = /\[\[([^|\]]+)(?:\|([^\]]+))?\]\]/g;

type WikiLinkNode = {
  type: 'wikiLink';
  data: {
    hName: string;
    hProperties: {
      slug: string;
      displayText: string;
    };
  };
  children: Array<{type: 'text'; value: string}>;
};

export const wikiLinkPlugin: Plugin<[], Root> = () => {
  return (tree: Root) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (!parent || index === undefined) return;

      const value = node.value;
      const matches = [...value.matchAll(wikiLinkRegex)];

      if (matches.length === 0) return;

      const children: Array<Text | WikiLinkNode> = [];
      let lastIndex = 0;

      for (const match of matches) {
        const [fullMatch, slug, displayText] = match;
        const matchIndex = match.index ?? 0;

        // Add text before the match
        if (matchIndex > lastIndex) {
          children.push({
            type: 'text',
            value: value.slice(lastIndex, matchIndex),
          });
        }

        // Add the wiki link node
        // Only set displayText if explicitly provided in the wiki-link syntax
        // This allows ArticleLink to fall back to the article's translated title
        const explicitDisplayText = displayText?.trim();
        const wikiLinkNode: WikiLinkNode = {
          type: 'wikiLink',
          data: {
            hName: 'article-link',
            hProperties: {
              slug: slug.trim(),
              displayText: explicitDisplayText || '',
            },
          },
          children: [
            {
              type: 'text',
              value: explicitDisplayText || slug.trim(),
            },
          ],
        };

        children.push(wikiLinkNode);
        lastIndex = matchIndex + fullMatch.length;
      }

      // Add remaining text after the last match
      if (lastIndex < value.length) {
        children.push({
          type: 'text',
          value: value.slice(lastIndex),
        });
      }

      // Replace the current node with the new nodes
      parent.children.splice(index, 1, ...(children as never[]));
    });
  };
};
